/**
 * Centralized API service for backend communication
 * Replaces mock data with real PostgreSQL data
 */

import {
  PayrollCase,
  DashboardStats,
  EntryItemsResponse,
  EmployeeGroup,
} from '../types';
import { API_CONFIG } from '../config/api';
import logger from '../utils/logger';

const API_BASE_URL = API_CONFIG.BASE_URL;

interface ApiResponse<T> {
  data?: T;
  error?: string;
  success: boolean;
  ocr_results?: T; // For OCR endpoint responses
}

interface CasesResponse {
  cases: PayrollCase[];
  total: number;
  filter: string;
  source: string;
}

interface CaseDetailsResponse {
  success: boolean;
  case: PayrollCase;
  payrollEntries: PayrollEntry[];
  documents: CaseDocument[];
  emails: CaseEmail[];
}

interface PayrollEntry {
  id: number;
  employeeName: string;
  employeeNumber?: string;
  employeeType?: 'hourly' | 'salary';

  // Hour types
  regularHours?: number;
  overtimeHours?: number;
  sickHours?: number;
  vacationHours?: number;
  holidayHours?: number;
  personalHours?: number;
  otherHours?: number;

  // Rates and pay
  hourlyRate?: number;
  salaryAmount?: number;
  grossPay: number;

  // Additional earnings
  bonusEarnings?: number;
  otherEarnings?: number;

  payPeriodStart?: string;
  payPeriodEnd?: string;
  department?: string;
  ocrConfidence: number;
  llmConfidence: number;
  hybridConfidence: number;
}

interface CaseDocument {
  id: number;
  filename: string;
  fileType: string;
  fileSize: number;
  filePath: string;
  downloadUrl: string;
  ocrConfidence: number;
  llmConfidence: number;
  hybridConfidence: number;
}

interface CaseEmail {
  id: number;
  subject: string;
  body: string;
  createdDate?: string;
}

interface ProcessingStatus {
  caseNumber: string;
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'n/a';
  currentStage: string | null;
}

interface ProcessingCasesResponse {
  cases: ProcessingStatus[];
}

class ApiService {
  private reauthTimerId: number | null = null;
  private tokenExpiresAt: number | null = null;

  // Global re-authentication state (prevents concurrent re-auth attempts)
  private isReauthenticating: boolean = false;
  private reauthPromise: Promise<boolean> | null = null;

  // Track initial auth check to prevent 401 handling during app load
  private isInitialAuthCheck: boolean = true;

  private boundVisibilityHandler: (() => void) | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initializeProactiveReauth();
      this.registerVisibilityHandler();
    }
  }

  /**
   * Re-check token expiry when a backgrounded tab becomes visible again.
   * Browsers throttle setTimeout in background tabs, so the proactive
   * re-auth timer may not fire on time. This catches that case.
   */
  private registerVisibilityHandler(): void {
    this.boundVisibilityHandler = () => {
      if (document.visibilityState !== 'visible') return;
      if (!this.tokenExpiresAt) return;

      const remainingMs = this.tokenExpiresAt - Date.now();
      if (remainingMs < 120_000) {
        logger.debug(
          `[Auth] Tab visible with ${Math.round(remainingMs / 1000)}s remaining — triggering re-auth`
        );
        this.handleReauthentication();
      }
    };
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        credentials: 'include', // ✅ CRITICAL: Send cookies with every request!
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      // Handle 401 Unauthorized with automatic re-authentication
      if (response.status === 401) {
        logger.info('[Auth] 401 Unauthorized');

        // Don't handle 401 during initial auth check (let AuthContext handle it)
        if (this.isInitialAuthCheck) {
          logger.info(
            '[Auth] 401 during initial auth check - letting AuthContext handle redirect'
          );
          throw new Error('Unauthorized - Initial auth check');
        }

        // Don't retry auth endpoints (prevents infinite loops)
        if (endpoint.startsWith('/api/auth/')) {
          logger.info('[Auth] 401 on auth endpoint - not retrying to avoid loop');
          throw new Error('Unauthorized - Auth endpoint');
        }

        // Attempt re-authentication (with global locking to prevent concurrent attempts)
        const authSuccess = await this.handleReauthentication();

        if (authSuccess) {
          // Re-authentication successful - retry the original request
          logger.info('[Auth] Re-auth successful, retrying request');
          return this.makeRequest(endpoint, options);
        } else {
          // Re-authentication failed - redirect to login
          logger.info('[Auth] Re-auth failed, redirecting to login');
          this.redirectToLogin();
          throw new Error('Unauthorized - Session expired');
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { data, success: true };
    } catch (error) {
      logger.error('API request failed:', error);
      return {
        error: error instanceof Error ? error.message : 'Unknown error',
        success: false,
      };
    }
  }

  /**
   * Initialize proactive re-authentication on app load
   * Checks if user is authenticated and starts timer if needed
   */
  private async initializeProactiveReauth(): Promise<void> {
    try {
      // Mark as initial auth check (prevents 401 handling during app load)
      this.isInitialAuthCheck = true;
      logger.debug('[Auth] Starting initial auth check...');

      // Get token expiration info from backend
      const response = await fetch(`${API_BASE_URL}/api/auth/token-info`, {
        credentials: 'include',
      });

      if (response.ok) {
        const tokenInfo = await response.json();
        logger.debug('[Auth] User authenticated, starting proactive re-auth timer');

        // Start timer with actual expires_in from backend
        this.startReauthTimer(tokenInfo.expires_in);
      } else {
        logger.debug('[Auth] No authenticated user or token expired');
      }
    } catch (error) {
      logger.debug('[Auth] Could not initialize proactive re-auth');
    } finally {
      // After initial check completes, enable 401 handling after 1 second
      // This gives AuthContext time to redirect if needed
      setTimeout(() => {
        this.isInitialAuthCheck = false;
        logger.debug('[Auth] Initial auth check complete - 401 handling enabled');
      }, 1000);
    }
  }

  /**
   * Handle re-authentication with global locking to prevent race conditions.
   * If re-auth is already in progress, wait for it instead of starting a new one.
   * This ensures only ONE re-auth attempt happens even if multiple API calls fail simultaneously.
   *
   * @returns Promise<boolean> - true if re-auth succeeded, false otherwise
   */
  private async handleReauthentication(): Promise<boolean> {
    // If re-auth is already in progress, wait for it
    if (this.isReauthenticating && this.reauthPromise) {
      logger.debug(
        '[Auth] Re-auth already in progress, waiting for existing attempt...'
      );
      return this.reauthPromise;
    }

    // Start new re-authentication attempt
    logger.debug('[Auth] Starting new re-authentication attempt');
    this.isReauthenticating = true;

    this.reauthPromise = (async () => {
      try {
        // Try silent re-authentication first
        const silentSuccess = await this.silentReauth();

        if (silentSuccess) {
          logger.debug('[Auth] Re-authentication successful');
          return true;
        } else {
          // Silent auth failed - will redirect to login in makeRequest
          logger.debug('[Auth] Re-authentication failed');
          return false;
        }
      } finally {
        // Clear re-auth state
        this.isReauthenticating = false;
        this.reauthPromise = null;
      }
    })();

    return this.reauthPromise;
  }

  /**
   * Redirect user to login page while preserving current location.
   * After successful login, user will be returned to the page they were viewing.
   */
  private redirectToLogin(): void {
    // Capture current location (path + query + hash)
    const currentPath =
      window.location.pathname + window.location.search + window.location.hash;
    const fullRedirectUrl = `${window.location.origin}${currentPath}`;
    const redirectUrl = encodeURIComponent(fullRedirectUrl);

    logger.debug('[Auth] Redirecting to login');

    // Redirect with return URL parameter
    window.location.href = `${API_BASE_URL}/api/auth/login?redirect_url=${redirectUrl}`;
  }

  /**
   * Silent re-authentication using hidden iframe with prompt=none
   * @returns true if successful, false if user needs to login
   */
  private async silentReauth(): Promise<boolean> {
    return new Promise(async (resolve) => {
      logger.debug('[Auth] Attempting silent re-authentication...');

      // REQUEST STORAGE ACCESS FIRST (for browsers that require it)
      // Modern browsers (Safari, Firefox) require explicit storage access permission
      // for cookies to work in iframe contexts, even with SameSite=None
      if ('requestStorageAccess' in document) {
        try {
          await (document as any).requestStorageAccess();
          logger.debug('[Auth] Storage access granted');
        } catch {
          logger.debug('[Auth] Storage access denied or not needed');
        }
      }

      // Create hidden iframe for silent auth
      const iframe = document.createElement('iframe');

      // Hidden iframe for silent authentication (production standard)
      iframe.style.display = 'none';
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';

      let timeoutId: number;
      let messageReceived = false;

      // Listen for postMessage from iframe
      const expectedOrigin = new URL(API_BASE_URL).origin;
      const messageHandler = (event: MessageEvent) => {
        // Security hardening: only accept postMessage events from the backend origin.
        if (event.origin !== expectedOrigin) {
          logger.debug(
            `[Auth] Ignoring postMessage from unexpected origin: ${event.origin}`
          );
          return;
        }

        // Security hardening: payload must be an object with a string type.
        const data = event.data;
        if (!data || typeof data !== 'object' || typeof data.type !== 'string') {
          logger.debug('[Auth] Ignoring malformed postMessage payload');
          return;
        }

        if (data.type === 'silent-auth-success') {
          messageReceived = true;
          clearTimeout(timeoutId);

          logger.debug('[Auth] Silent re-authentication successful');

          // Cleanup
          window.removeEventListener('message', messageHandler);
          document.body.removeChild(iframe);

          // Restart timer with new expires_in
          if (data.expires_in) {
            this.startReauthTimer(data.expires_in);
          }

          resolve(true);
        } else if (data.type === 'silent-auth-failed') {
          messageReceived = true;
          clearTimeout(timeoutId);

          logger.debug('[Auth] Silent auth failed - redirecting to login');

          // Cleanup immediately
          window.removeEventListener('message', messageHandler);
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          resolve(false);
        } else if (data.type === 'role-unauthorized') {
          // Role was revoked during an active session — the backend already
          // deleted the cookies.  Redirect to the dead-end unauthorized page
          // (NOT login — re-authenticating won't help if the role is missing).
          messageReceived = true;
          clearTimeout(timeoutId);

          logger.debug(
            '[Auth] Role authorization revoked during silent auth — redirecting to /unauthorized'
          );

          // Cleanup immediately
          window.removeEventListener('message', messageHandler);
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }

          window.location.href = '/unauthorized?reason=role_revoked';
          resolve(false);
        }
      };

      window.addEventListener('message', messageHandler);

      // Set timeout for silent auth (10 seconds)
      timeoutId = window.setTimeout(() => {
        if (!messageReceived) {
          logger.debug('[Auth] Silent auth timed out (10s)');
          window.removeEventListener('message', messageHandler);
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          resolve(false);
        }
      }, 10000);

      // Load silent auth URL in iframe
      const silentAuthUrl = new URL('/api/auth/login', API_BASE_URL);
      silentAuthUrl.searchParams.set('prompt', 'none');

      // Add error handler to detect iframe load failures
      iframe.onerror = () => {
        logger.debug('[Auth] iframe failed to load');
        if (!messageReceived) {
          messageReceived = true;
          clearTimeout(timeoutId);
          window.removeEventListener('message', messageHandler);
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          resolve(false);
        }
      };

      iframe.src = silentAuthUrl.toString();
      document.body.appendChild(iframe);
    });
  }

  /**
   * Start proactive re-authentication timer
   * @param expiresIn - Seconds until token expires (from backend)
   */
  private startReauthTimer(expiresIn: number): void {
    // Clear any existing timer
    this.stopReauthTimer();

    // Calculate when token will expire
    this.tokenExpiresAt = Date.now() + expiresIn * 1000;

    // Refresh at 75% of token lifetime (or at least 60s before expiry)
    const reauthBuffer = Math.max(Math.floor(expiresIn * 0.25), 120);
    const reauthInSeconds = Math.max(expiresIn - reauthBuffer, 60);
    const reauthInMs = reauthInSeconds * 1000;

    logger.debug(`[Auth] Re-auth timer set for ${reauthInSeconds}s`);

    // Set timer to attempt silent re-auth before token expires
    this.reauthTimerId = window.setTimeout(async () => {
      logger.debug('[Auth] Proactive re-authentication triggered');

      // Try silent authentication first
      const silentSuccess = await this.silentReauth();

      if (!silentSuccess) {
        // Silent auth failed - SSO session expired, need full login
        logger.debug('[Auth] Silent auth failed, redirecting to login page...');

        // Preserve current location to return after login
        const currentPath =
          window.location.pathname + window.location.search + window.location.hash;
        const fullRedirectUrl = `${window.location.origin}${currentPath}`;
        const redirectUrl = encodeURIComponent(fullRedirectUrl);

        window.location.href = `${API_BASE_URL}/api/auth/login?redirect_url=${redirectUrl}`;
      }
      // If successful, silentReauth() already restarted the timer
    }, reauthInMs);
  }

  /**
   * Stop the re-authentication timer
   */
  private stopReauthTimer(): void {
    if (this.reauthTimerId) {
      clearTimeout(this.reauthTimerId);
      this.reauthTimerId = null;
      this.tokenExpiresAt = null;
      logger.debug('[Auth] Re-authentication timer stopped');
    }
  }

  /**
   * Get time remaining until token expires (in seconds)
   * @returns Seconds until expiry, or null if unknown
   */
  private getTimeUntilExpiry(): number | null {
    if (!this.tokenExpiresAt) {
      return null;
    }
    return Math.max(0, Math.floor((this.tokenExpiresAt - Date.now()) / 1000));
  }

  /**
   * Fetch all cases from PostgreSQL database
   */
  async getCases(status: string = 'all', limit: number = 1000): Promise<PayrollCase[]> {
    // TEMPORARY: Increased to 1000 for testing
    const result = await this.makeRequest<CasesResponse>(
      `/api/cases?status=${status}&limit=${limit}`
    );

    if (result.success && result.data) {
      logger.debug(`[API] Loaded ${result.data.cases.length} cases`);
      return result.data.cases;
    } else {
      logger.error('[API] Failed to load cases');
      return [];
    }
  }

  /**
   * Fetch single case by case number with detailed information
   */
  async getCase(caseNumber: string): Promise<PayrollCase | null> {
    const result = await this.makeRequest<CaseDetailsResponse>(
      `/api/cases/${caseNumber}`
    );

    if (result.success && result.data) {
      logger.debug(
        `[API] Loaded case with ${result.data.payrollEntries.length} entries`
      );
      return result.data.case;
    } else {
      logger.warn('[API] Case not found');
      return null;
    }
  }

  /**
   * Fetch case details including payroll entries and documents
   */
  async getCaseDetails(caseNumber: string): Promise<CaseDetailsResponse | null> {
    const result = await this.makeRequest<CaseDetailsResponse>(
      `/api/cases/${caseNumber}`
    );

    if (result.success && result.data) {
      logger.debug('[API] Case details loaded');
      return result.data;
    } else {
      logger.error('[API] Failed to load case details');
      return null;
    }
  }

  /**
   * Fetch cases assigned to specific user
   */
  async getCasesByUser(userName: string): Promise<PayrollCase[]> {
    const allCases = await this.getCases();
    const userCases = allCases.filter((c) => c.assignedTo === userName);

    logger.debug(`[API] Found ${userCases.length} cases for user`);
    return userCases;
  }

  /**
   * Get current authenticated user from server
   * This validates the httpOnly cookie and returns user info
   */
  async getCurrentUser(): Promise<{
    sub: string;
    email: string | null;
    name: string | null;
    given_name: string | null;
    family_name: string | null;
    roles: string[];
    permissions: string[];
    groups: string[];
  } | null> {
    const result = await this.makeRequest<{
      sub: string;
      email: string | null;
      name: string | null;
      given_name: string | null;
      family_name: string | null;
      roles: string[];
      permissions: string[];
      groups: string[];
    }>('/api/auth/user');

    if (result.success && result.data) {
      logger.debug('[Auth] User authenticated');
      return result.data;
    } else {
      logger.warn('[Auth] User not authenticated or session expired');
      return null;
    }
  }

  /**
   * Fetch cases by confidence level
   */
  async getCasesByConfidence(level: 'high' | 'medium' | 'low'): Promise<PayrollCase[]> {
    const allCases = await this.getCases();

    let filteredCases: PayrollCase[];
    switch (level) {
      case 'high':
        filteredCases = allCases.filter((c) => c.aiConfidence >= 0.85);
        break;
      case 'medium':
        filteredCases = allCases.filter(
          (c) => c.aiConfidence >= 0.7 && c.aiConfidence < 0.85
        );
        break;
      case 'low':
        filteredCases = allCases.filter((c) => c.aiConfidence < 0.7);
        break;
      default:
        filteredCases = allCases;
    }

    logger.debug(`[API] Found ${filteredCases.length} ${level} confidence cases`);
    return filteredCases;
  }

  /**
   * Generate dashboard stats from real case data
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const cases = await this.getCases();

    const stats: DashboardStats = {
      totalCases: cases.length,
      openCases: cases.filter(
        (c) => c.status === 'Pending' || c.status === 'In Progress'
      ).length,
      resolvedCases: cases.filter((c) => c.status === 'Completed').length,
      averageResolutionTime: '2.3 days', // TODO: Calculate from real data
    };

    logger.debug('[API] Dashboard stats generated');
    return stats;
  }

  /**
   * Process attachment with OCR
   */
  async processAttachmentOCR(attachmentId: number): Promise<any> {
    const result = await this.makeRequest<any>(
      `/api/attachments/${attachmentId}/process-ocr`,
      { method: 'POST' }
    );

    if (
      result.success &&
      result.data &&
      result.data.success &&
      result.data.ocr_results
    ) {
      logger.debug('[API] OCR processing completed');
      return result.data.ocr_results;
    } else {
      logger.error('[API] OCR processing failed');
      throw new Error(result.error || result.data?.error || 'OCR processing failed');
    }
  }

  /**
   * Get attachment processing status
   */
  async getAttachmentStatus(attachmentId: number): Promise<any> {
    const result = await this.makeRequest<any>(
      `/api/attachments/${attachmentId}/status`,
      { method: 'GET' }
    );

    if (result.success && result.data) {
      // Return the full response including status_info and ocr_results
      return result.data;
    } else {
      logger.error('[API] Failed to get attachment status');
      throw new Error(result.error || 'Failed to get attachment status');
    }
  }

  /**
   * Preview spreadsheet (CSV/Excel) data
   */
  async previewSpreadsheet(attachmentId: number, maxRows: number = 100): Promise<any> {
    const result = await this.makeRequest<any>(
      `/api/attachments/${attachmentId}/preview?max_rows=${maxRows}`,
      { method: 'GET' }
    );

    if (result.success && result.data) {
      logger.debug('[API] Spreadsheet preview loaded');
      return result.data;
    } else {
      logger.error('[API] Failed to preview spreadsheet');
      throw new Error(result.error || 'Failed to preview spreadsheet');
    }
  }

  /**
   * Retry failed attachment processing
   */
  async retryAttachmentProcessing(attachmentId: number): Promise<any> {
    const result = await this.makeRequest<any>(
      `/api/attachments/${attachmentId}/retry`,
      { method: 'POST' }
    );

    if (result.success && result.data) {
      logger.debug('[API] Retry initiated');
      return result.data;
    } else {
      logger.error('[API] Failed to retry attachment processing');
      throw new Error(result.error || 'Failed to retry attachment processing');
    }
  }

  /**
   * Test database connectivity
   */
  async testConnection(): Promise<boolean> {
    const result = await this.makeRequest('/api/health/database');
    return result.success;
  }

  /**
   * Pull cases from Dynamics 365 webhook
   *
   * @param options - Optional parameters for case pulling:
   *   - caseNumbers: Array of specific case numbers to pull (e.g., ["CA-6060133"])
   *   - days: Number of days to look back (default: 180)
   *   - maxCases: Maximum number of cases to pull (default: 3)
   *   - caseOriginCode: Filter by case origin (1=Phone, 2=Email, 3=Web)
   *
   * If no options provided, pulls recent cases using default filters.
   */
  async pullCasesFromDynamics(options?: {
    caseNumbers?: string[];
    days?: number;
    maxCases?: number;
    caseOriginCode?: number;
  }): Promise<{
    success: boolean;
    message: string;
    casesSuccessful: number;
    casesFound: number;
    totalEmails: number;
    totalAttachments: number;
    totalFileSize: number;
    durationSeconds: number;
    caseResults: Array<{
      caseNumber: string;
      caseId: string;
      success: boolean;
      emailsProcessed: number;
      attachmentsDownloaded: number;
      attachmentsFailed: number;
      totalFileSize: number;
      errors: string[];
    }>;
    errors: string[];
  }> {
    logger.debug('[API] Pulling cases from Dynamics...');

    // Build request body if options provided
    let requestBody = undefined;
    if (options) {
      requestBody = {
        case_numbers: options.caseNumbers,
        days: options.days,
        max_cases: options.maxCases,
        case_origin_code: options.caseOriginCode,
      };
    }

    const result = await this.makeRequest<any>(`/api/dynamics/webhooks/cases`, {
      method: 'POST',
      ...(requestBody && {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }),
    });

    if (result.success && result.data) {
      logger.debug(`[API] Pulled ${result.data.cases_successful} cases from Dynamics`);
      return {
        success: result.data.success,
        message: result.data.message,
        casesSuccessful: result.data.cases_successful,
        casesFound: result.data.cases_found,
        totalEmails: result.data.total_emails,
        totalAttachments: result.data.total_attachments_downloaded,
        totalFileSize: result.data.total_file_size,
        durationSeconds: result.data.duration_seconds,
        caseResults: result.data.case_results || [],
        errors: result.data.errors || [],
      };
    } else {
      logger.error('[API] Failed to pull cases from Dynamics');
      throw new Error(result.error || 'Failed to pull cases from Dynamics');
    }
  }

  /**
   * Get processing status for cases with attachments being processed.
   *
   * This is a lightweight endpoint for polling that returns only cases
   * with attachments currently being processed (status = 'pending' or 'processing').
   *
   * Used by the Dashboard to show spinner and status updates on Review buttons.
   *
   * @param userName - Username to filter assigned cases
   * @returns Array of cases with their processing status
   */
  async getProcessingCases(userName: string): Promise<ProcessingStatus[]> {
    const result = await this.makeRequest<ProcessingCasesResponse>(
      `/api/cases/processing?user=${encodeURIComponent(userName)}`
    );

    if (result.success && result.data) {
      logger.debug(
        `[API] Found ${result.data.cases.length} cases with active processing`
      );
      return result.data.cases;
    } else {
      logger.error('[API] Failed to fetch processing cases');
      return [];
    }
  }

  /**
   * Get the SSE URL for reprocessing a case.
   *
   * Note: SSE endpoints require EventSource (not fetch), so this returns the URL
   * for direct use with `new EventSource(url, { withCredentials: true })`.
   *
   * @param caseNumber - Case number to reprocess (e.g., "CA-6060519")
   * @returns Full URL for the SSE endpoint
   */
  getReprocessCaseStreamUrl(caseNumber: string): string {
    return `${API_BASE_URL}/api/dynamics/reprocess/${encodeURIComponent(caseNumber)}/stream`;
  }

  /**
   * Get the SSE URL for initial processing of a case.
   *
   * Used when a case hasn't been processed yet (is_processed = false).
   * Unlike reprocess, this does NOT delete existing data first.
   *
   * Note: SSE endpoints require EventSource (not fetch), so this returns the URL
   * for direct use with `new EventSource(url, { withCredentials: true })`.
   *
   * @param caseNumber - Case number to process (e.g., "CA-6060519")
   * @returns Full URL for the SSE endpoint
   */
  getInitialProcessCaseStreamUrl(caseNumber: string): string {
    return `${API_BASE_URL}/api/dynamics/initial-process/${encodeURIComponent(caseNumber)}/stream`;
  }

  /**
   * Update payroll entries for a case (bulk save).
   *
   * CSRs can edit multiple entries at once and save them all together.
   * All changes are logged to the processing_logs table for audit trail.
   *
   * @param caseNumber - Case number (e.g., "CA-6060519")
   * @param entries - Array of modified payroll entries to save
   * @param removedEntries - Array of entries marked for removal
   * @returns Promise with success status and message
   */
  async updatePayrollEntries(
    caseNumber: string,
    entries: any[],
    removedEntries: any[]
  ): Promise<{
    success: boolean;
    message: string;
    updated_count: number;
    removed_count: number;
  }> {
    // Filter out new entries that are marked for deletion - no point creating just to delete
    const entriesToSend = entries.filter((entry) => {
      const parsedId = parseInt(entry.id);
      const isNewEntry = isNaN(parsedId) || String(entry.id).startsWith('manual_');
      const isDeleted = entry.deletedByAgent === true;

      // Skip new entries that are already deleted
      if (isNewEntry && isDeleted) {
        return false;
      }
      return true;
    });

    logger.debug(
      `[API] Saving ${entriesToSend.length} entries, removing ${removedEntries.length}`
    );

    // Map frontend entries to backend format
    const updates = entriesToSend.map((entry) => {
      // Check if this is a new entry (temporary string ID starting with 'manual_')
      const parsedId = parseInt(entry.id);
      const isNewEntry = isNaN(parsedId) || String(entry.id).startsWith('manual_');

      return {
        id: isNewEntry ? null : parsedId, // null for new entries, numeric ID for existing
        attachment_id: entry.attachmentId ?? null, // Document ID this entry belongs to
        employee_name: entry.employeeName,
        employee_id: entry.employeeId,
        employee_type: entry.employeeType,
        // Hour types
        regular_hours: entry.regularHours,
        overtime_hours: entry.overtimeHours,
        sick_hours: entry.sickHours ?? 0,
        vacation_hours: entry.vacationHours ?? 0,
        holiday_hours: entry.holidayHours ?? 0,
        personal_hours: entry.personalHours ?? 0,
        other_hours: entry.otherHours ?? 0,
        // Rates and pay
        hourly_rate: entry.regularRate,
        salary_amount: entry.salaryAmount ?? 0,
        gross_pay: entry.totalPay,
        // Additional earnings
        bonus_earnings: entry.bonusEarnings ?? 0,
        other_earnings: entry.otherEarnings ?? 0,
        // Soft delete flag - if true, entry will be marked as deleted
        deleted_by_agent: entry.deletedByAgent ?? false,
        is_removed: entry.deletedByAgent ?? false, // Keep for backwards compatibility
      };
    });

    // Mark removed entries (legacy support)
    const removals = removedEntries.map((entry) => ({
      id: parseInt(entry.id),
      is_removed: true,
      deleted_by_agent: true,
    }));

    const allUpdates = [...updates, ...removals];

    const result = await this.makeRequest<any>(`/api/cases/${caseNumber}/entries`, {
      method: 'PUT',
      body: JSON.stringify({
        entries: allUpdates,
        reason: 'CSR reviewed and edited payroll data',
      }),
    });

    if (result.success && result.data) {
      logger.debug(
        `[API] Saved ${result.data.updated_count} entries, removed ${result.data.removed_count}`
      );
      return {
        success: true,
        message: result.data.message,
        updated_count: result.data.updated_count,
        removed_count: result.data.removed_count,
      };
    } else {
      logger.error('[API] Failed to save payroll entries');
      throw new Error(result.error || 'Failed to save entries');
    }
  }

  /**
   * Send a case for manual processing.
   *
   * This action is IRREVERSIBLE. Once a case is marked for manual processing:
   * - The case becomes locked and read-only
   * - No further modifications can be made through the system
   * - The case is removed from automated processing queue
   *
   * @param caseNumber - Case number (e.g., "CA-6060519")
   * @param reason - Reason for manual processing (from allowed list)
   * @param comment - Optional additional comment (required if reason is 'other')
   * @returns Promise with success status and details
   */
  async sendForManualProcessing(
    caseNumber: string,
    reason: string,
    comment?: string
  ): Promise<{
    success: boolean;
    message: string;
    case_number: string;
    reason: string;
    processed_by: string;
    created_at: string;
  }> {
    logger.debug('[API] Sending case for manual processing');

    const result = await this.makeRequest<any>(
      `/api/cases/${caseNumber}/manual-processing`,
      {
        method: 'POST',
        body: JSON.stringify({
          reason,
          comment: comment || null,
        }),
      }
    );

    if (result.success && result.data) {
      logger.debug('[API] Case sent for manual processing');
      return {
        success: result.data.success,
        message: result.data.message,
        case_number: result.data.case_number,
        reason: result.data.reason,
        processed_by: result.data.processed_by,
        created_at: result.data.created_at,
      };
    } else {
      logger.error('[API] Failed to send case for manual processing');
      throw new Error(result.error || 'Failed to send case for manual processing');
    }
  }

  /**
   * Persist user feedback collected by the 'Before We Process' modal.
   *
   * Skip submissions are also persisted (was_skipped=true) so analytics can
   * measure response rates. Multiple submissions per case are allowed.
   *
   * The persisted row is independent of the payroll_cases lifecycle —
   * it survives case deletion and the 90-day cleanup procedure.
   *
   * @param caseNumber - Case number (e.g., "CA-6060519")
   * @param payload - Feedback payload matching PayrollFeedbackRequest on the backend
   * @returns Promise resolving to the persisted feedback record
   */
  async submitPayrollFeedback(
    caseNumber: string,
    payload: {
      was_skipped: boolean;
      manual_new_hire: boolean;
      manual_termination: boolean;
      salary_blocked_in_core: boolean;
      deductions_missed: boolean;
      hours_or_attachments_incorrect: boolean;
      pay_agent_hallucinated: boolean;
      entire_process_failed: boolean;
      additional_details: string | null;
    }
  ): Promise<{
    success: boolean;
    case_number: string;
    feedback_id: number;
    submitted_by: string;
    was_skipped: boolean;
    created_at: string;
  }> {
    logger.debug('[API] Submitting payroll feedback');

    const result = await this.makeRequest<any>(
      `/api/cases/${caseNumber}/payroll-feedback`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );

    if (result.success && result.data) {
      logger.debug('[API] Payroll feedback recorded');
      return {
        success: result.data.success,
        case_number: result.data.case_number,
        feedback_id: result.data.feedback_id,
        submitted_by: result.data.submitted_by,
        was_skipped: result.data.was_skipped,
        created_at: result.data.created_at,
      };
    } else {
      logger.error('[API] Failed to submit payroll feedback');
      throw new Error(result.error || 'Failed to submit payroll feedback');
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Template-driven entry items CRUD (payroll_entry_items table)
  // ═══════════════════════════════════════════════════════════════════════════

  async getEntryItems(caseNumber: string): Promise<EntryItemsResponse | null> {
    const result = await this.makeRequest<EntryItemsResponse>(
      `/api/cases/${caseNumber}/entry-items`
    );
    if (result.success && result.data) {
      return result.data;
    }
    logger.error('[API] Failed to load entry items');
    return null;
  }

  async updateEntryItemCell(
    caseNumber: string,
    itemId: string,
    value: string | null,
    isHoursMinutes?: boolean
  ): Promise<{
    success: boolean;
    id: string;
    value: string | null;
    isHoursMinutes: boolean;
  }> {
    const body: { value: string | null; isHoursMinutes?: boolean } = { value };
    if (isHoursMinutes !== undefined) {
      body.isHoursMinutes = isHoursMinutes;
    }
    const result = await this.makeRequest<{
      success: boolean;
      id: string;
      value: string | null;
      isHoursMinutes: boolean;
    }>(`/api/cases/${caseNumber}/entry-items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    if (result.success && result.data) return result.data;
    throw new Error(result.error || 'Failed to update cell');
  }

  async assignEmployeeToGroup(
    caseNumber: string,
    employeeGroupId: number,
    payload: {
      employeeId?: string | null;
      employeeName?: string | null;
      employeeNumber?: string | null;
      employeeType?: string | null;
    }
  ): Promise<{ success: boolean; rowsUpdated: number }> {
    const result = await this.makeRequest<{
      success: boolean;
      employeeGroupId: number;
      rowsUpdated: number;
    }>(`/api/cases/${caseNumber}/entry-items/group/${employeeGroupId}/employee`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    if (result.success && result.data) return result.data;
    throw new Error(result.error || 'Failed to assign employee');
  }

  async deleteEmployeeGroup(
    caseNumber: string,
    employeeGroupId: number
  ): Promise<{ success: boolean; rowsDeleted: number }> {
    const result = await this.makeRequest<{
      success: boolean;
      employeeGroupId: number;
      rowsDeleted: number;
    }>(`/api/cases/${caseNumber}/entry-items/group/${employeeGroupId}`, {
      method: 'DELETE',
    });
    if (result.success && result.data) return result.data;
    throw new Error(result.error || 'Failed to delete employee group');
  }

  async addEmployee(
    caseNumber: string,
    payload: {
      employeeName?: string | null;
      employeeType?: string | null;
      employeeId?: string | null;
      employeeNumber?: string | null;
      payGroupId?: number | null;
      payGroupName?: string | null;
    }
  ): Promise<{ success: boolean; employeeGroupId: number; rowsCreated: number }> {
    const result = await this.makeRequest<{
      success: boolean;
      employeeGroupId: number;
      rowsCreated: number;
    }>(`/api/cases/${caseNumber}/entry-items/employee`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (result.success && result.data) return result.data;
    throw new Error(result.error || 'Failed to add employee');
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;

// Export types for use in components
export type { ProcessingStatus };
